<template>
  <div class="ac-card" v-loading="loading">
    <div class="ac-panel-head">
      <div>
        <h2 class="ac-panel-title">Users</h2>
        <p class="ac-panel-desc">{{ resultSummary }}</p>
      </div>
      <button class="ac-btn ac-btn--sm" @click="inviteUsers">
        + Invite User
      </button>
    </div>

    <div class="ac-card-body tm-body">
      <div class="xo-toolbar">
        <div class="xo-field xo-field--search">
          <svg
            class="xo-field-icon"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            id="team-search"
            v-model="search"
            type="search"
            class="xo-search"
            placeholder="Search users by name or email"
            aria-label="Search users"
          />
        </div>
        <button v-if="search" class="xo-reset" @click="search = ''">
          Reset
        </button>
      </div>

      <div class="pg-scroll">
        <div class="pg-table">
          <div class="pg-thead">
            <div class="pg-th">Name</div>
            <div class="pg-th">Phone</div>
            <div class="pg-th">Email</div>
            <div class="pg-th">Role</div>
            <div class="pg-th pg-th--action"></div>
          </div>

          <div class="pg-tbody">
            <div v-for="(row, i) in filterTableData" :key="i" class="pg-row-wrap">
              <div class="pg-row">
                <div class="pg-td">
                  <div class="pg-user-cell">
                    <div class="pg-avatar">
                      {{ row.user_name?.charAt(0)?.toUpperCase() }}
                    </div>
                    <span class="pg-user-name">{{ row.user_name }}</span>
                  </div>
                </div>
                <div class="pg-td">
                  <span class="pg-td-muted">{{ row.user_phone }}</span>
                </div>
                <div class="pg-td">
                  <span class="pg-td-muted" :title="row.user_email">{{ row.user_email }}</span>
                </div>
                <div class="pg-td">
                  <span
                    class="pg-role-badge"
                    :class="
                      row.user_type === 'Admin'
                        ? 'pg-role--admin'
                        : 'pg-role--normal'
                    "
                  >
                    {{ row.user_type }}
                  </span>
                </div>
                <div class="pg-td pg-td--action">
                  <button class="pg-edit-link" @click="handleEdit(i, row)">
                    Edit
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pg-empty" v-if="!loading && filterTableData.length === 0">
        <div class="pg-empty-text">
          {{ search ? "No users match your search" : "No users found" }}
        </div>
        <button class="ac-btn ac-btn--ghost ac-btn--sm" @click="inviteUsers">
          Invite your first user
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, reactive } from "vue";
import router from "../../router";
import axios from "axios";
import { ElMessage } from "element-plus";
import { useAuthStore } from "../../store/authStore";
import { isCompanyAdminFromToken } from "../../utils/authRole";

const loading = ref(false);
const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

const ensureAdminAccess = () => {
  if (!isCompanyAdminFromToken(localStorage.getItem("_panda"))) {
    router.replace("/request");
    return false;
  }
  return true;
};

interface User {
  user_name: string;
  user_phone: string;
  user_email: string;
  user_id: number;
  user_type: string;
}
const tableData = reactive<User[]>([]);
const search = ref("");

const filterTableData = computed(() =>
  tableData.filter(
    (d) =>
      !search.value ||
      d.user_name?.toLowerCase().includes(search.value.toLowerCase()) ||
      d.user_email?.toLowerCase().includes(search.value.toLowerCase()),
  ),
);

// Header subtitle — makes it obvious when the search is hiding rows.
const resultSummary = computed(() => {
  if (loading.value) return "Loading users…";
  const shown = filterTableData.value.length;
  const total = tableData.length;
  const noun = shown === 1 ? "user" : "users";
  if (search.value && shown !== total) return `${shown} ${noun} of ${total}`;
  return `${shown} ${noun}`;
});

const handleEdit = (index: number, row: User) => {
  store.setCurrentEditedUser(row);
  router.push(`/account/team/${row.user_id}/edit`);
};

const inviteUsers = () => router.push("/account/team/invite");

const postData = async () => {
  if (!ensureAdminAccess()) return;
  loading.value = true;
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_ONBOARDING_SERVICE}getusers?token=${store.getToken}`,
    );
    if (response.status == 200) {
      if (Array.isArray(response.data)) {
        response.data.forEach((el: any) => {
          // Backfill missing name/phone from legacy fields before mapping
          if (!el.user_name && el.name) el.user_name = el.name;
          if (!el.user_phone && el.phone) el.user_phone = el.phone;
          el.user_type = el.user_type == 1 ? "Normal" : "Admin";
          tableData.push(el);
        });
      } else {
        ElMessage({
          message: "Session expired. Please log in again.",
          type: "error",
        });
        logwat();
      }
    } else {
      ElMessage({ message: "Failed to load users.", type: "error" });
    }
  } catch {
    ElMessage({ message: "Failed to load users.", type: "error" });
  }
  loading.value = false;
};
void postData();

const logwat = () => {
  store.setAuthStatus(false);
  store.$reset();
  localStorage.removeItem("_panda");
  router.push("/login");
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap");
* {
  box-sizing: border-box;
}
/* Card chrome lives in styles/account.scss and the search toolbar in
   styles/list-page.scss. Only this page's table styles are below. */
.tm-body {
  padding-top: 20px;
}
.pg-scroll {
  overflow-x: auto;
}
.pg-table {
  width: 100%;
  min-width: 640px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}
.pg-thead {
  display: grid;
  grid-template-columns: 1fr 160px 1fr 120px 100px;
  background: #f8fafc;
  border-bottom: 1.5px solid #e2e8f0;
  padding: 0 20px;
}
.pg-th {
  font-size: 0.68rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 10px;
  white-space: nowrap;
}
.pg-th--action {
  text-align: right;
}
.pg-tbody {
  display: flex;
  flex-direction: column;
}
.pg-row-wrap {
  border-bottom: 1px solid #f1f5f9;
}
.pg-row-wrap:last-child {
  border-bottom: none;
}
.pg-row {
  display: grid;
  grid-template-columns: 1fr 160px 1fr 120px 100px;
  align-items: center;
  padding: 0 20px;
  height: 58px;
  transition: background 0.12s;
}
.pg-row:hover {
  background: #f8fafc;
}
.pg-td {
  padding: 0 10px;
  overflow: hidden;
}
.pg-td--action {
  text-align: right;
}
.pg-user-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.pg-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(135deg, #12284a 0%, #1e3f73 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
}
.pg-user-name {
  font-size: 0.84rem;
  font-weight: 600;
  color: #12284a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pg-td-muted {
  font-size: 0.81rem;
  color: #64748b;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}
.pg-role-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.73rem;
  font-weight: 600;
  white-space: nowrap;
  border: 1.5px solid currentColor;
}
.pg-role--admin {
  color: #6d28d9;
  background: #f5f3ff;
  border-color: #ddd6fe;
}
.pg-role--normal {
  color: #2563eb;
  background: #eff6ff;
  border-color: #93c5fd;
}
.pg-edit-link {
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
  transition: gap 0.15s;
  white-space: nowrap;
}
.pg-row:hover .pg-edit-link {
  gap: 7px;
}
.pg-empty {
  text-align: center;
  padding: 40px 0 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.pg-empty-text {
  font-size: 1rem;
  font-weight: 500;
  color: #94a3b8;
}
</style>
